/**
 * DosyaKaynakSecici — "Fotoğraf çek" ya da "Dosyadan yükle" seçimi (backlog #727; ilk kullanım: denetmen tarafındaki Kişi belgeleri).
 *
 * İki parça, ikisi de aynı iç kancayı kullanır:
 *   <DosyaKaynakDugmeleri />  iki düğme yan yana (yükleme penceresinin içinde, pencerenin kendisi "belge" bağlamı olduğunda).
 *   <DosyaKaynakSecici />     "Yükle" düğmesine basınca açılan küçük seçim penceresi (`acik` / `onKapat` ile denetlenir).
 *
 * "Dosyadan yükle" her zaman sistemin dosya penceresini açar (`accept` yer sahibinin kendi listesi; çoklu seçim `multiple` ile).
 * "Fotoğraf çek": telefon / tablette (dokunmatik) kameranın kendi uygulaması (`capture="environment"`); masaüstünde web kamerası
 * penceresi (getUserMedia). Kamera yoksa ya da izin verilmediyse pencere nedenini yazar ve dosyadan yüklemeyi sunar.
 * Çekilen resim JPEG bir `File` olarak aynı `onDosya` yoluna girer; yer sahibinin boyut / tür denetimi aynen çalışır.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Camera, FolderOpen } from "lucide-react";
import { useTranslation } from "react-i18next";

const dokunmatikMi = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
const webKameraVarMi = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

function WebKameraPenceresi({ onKapat, onFoto, onDosyadan }) {
  const { t } = useTranslation("common");
  const videoRef = useRef(null);
  const [akis, setAkis] = useState(null);
  const [hata, setHata] = useState(null); // "izin" | "yok"

  useEffect(() => {
    let iptal = false;
    let acilan = null;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((a) => {
        if (iptal) { a.getTracks().forEach((p) => p.stop()); return; }
        acilan = a;
        setAkis(a);
      })
      .catch((e) => { if (!iptal) setHata(e && (e.name === "NotAllowedError" || e.name === "SecurityError") ? "izin" : "yok"); });
    return () => { iptal = true; if (acilan) acilan.getTracks().forEach((p) => p.stop()); };
  }, []);

  // Pencere içeriği açılış animasyonundan sonra bağlanır; akış hazır olunca ya da video öğesi gelince kendiliğinden eşlenir.
  const videoBagla = useCallback((el) => {
    videoRef.current = el;
    if (el && akis) el.srcObject = akis;
  }, [akis]);

  function cek() {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const tuval = document.createElement("canvas");
    tuval.width = v.videoWidth;
    tuval.height = v.videoHeight;
    tuval.getContext("2d").drawImage(v, 0, 0);
    tuval.toBlob((blob) => {
      if (!blob) { setHata("yok"); return; }
      onFoto(new File([blob], `foto-${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`, { type: "image/jpeg" }));
    }, "image/jpeg", 0.92);
  }

  return (
    <Modal isOpen toggle={onKapat} centered>
      <ModalHeader toggle={onKapat}>{t("dosyaKaynagi.fotografCek")}</ModalHeader>
      <ModalBody>
        {hata ? (
          <Alert fade={false} color="warning" className="py-2 small mb-0" data-testid="web-kamera-hata">
            {t(hata === "izin" ? "dosyaKaynagi.kameraIzniYok" : "dosyaKaynagi.kameraAcilamadi")}
          </Alert>
        ) : (
          <>
            <video ref={videoBagla} autoPlay playsInline muted className="w-100 rounded bg-dark" style={{ minHeight: 180 }} data-testid="web-kamera-video" />
            {!akis && <div className="small text-muted mt-1">{t("dosyaKaynagi.kameraAciliyor")}</div>}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onKapat}>{t("dosyaKaynagi.vazgec")}</Button>
        {hata ? (
          <Button color="primary" onClick={onDosyadan}><FolderOpen size={15} className="me-1" />{t("dosyaKaynagi.dosyadanYukle")}</Button>
        ) : (
          <Button color="primary" disabled={!akis} onClick={cek} data-testid="web-kamera-cek"><Camera size={15} className="me-1" />{t("dosyaKaynagi.cek")}</Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

// Gizli girdiler + web kamerası penceresi. Girdiler seçim penceresinin DIŞINDA durur: pencere kapanınca dosya penceresinin sonucu kaybolmaz.
function useDosyaGirdileri({ accept, multiple, onDosya, onDosyalar }) {
  const dosyaRef = useRef(null);
  const kameraRef = useRef(null);
  const [webKamera, setWebKamera] = useState(false);

  const ilet = (liste) => {
    if (liste.length === 0) return;
    if (multiple) onDosyalar?.(liste); else onDosya?.(liste[0]);
  };
  const secildi = (e) => {
    const liste = Array.from(e.target.files ?? []);
    e.target.value = ""; // aynı dosya yeniden seçilebilsin
    ilet(liste);
  };
  const dosyadan = () => dosyaRef.current?.click();
  const kameradan = () => {
    if (!dokunmatikMi() && webKameraVarMi()) setWebKamera(true);
    else kameraRef.current?.click();
  };

  const girdiler = (
    <>
      <input ref={dosyaRef} type="file" className="d-none" accept={accept} multiple={multiple} onChange={secildi} data-testid="dosya-kaynak-dosya" />
      <input ref={kameraRef} type="file" className="d-none" accept="image/*" capture="environment" onChange={secildi} data-testid="dosya-kaynak-kamera" />
      {webKamera && (
        <WebKameraPenceresi
          onKapat={() => setWebKamera(false)}
          onFoto={(f) => { setWebKamera(false); ilet([f]); }}
          onDosyadan={() => { setWebKamera(false); dosyadan(); }}
        />
      )}
    </>
  );
  return { dosyadan, kameradan, girdiler };
}

export function DosyaKaynakDugmeleri({ accept, multiple = false, onDosya, onDosyalar, disabled = false, size = "sm", className = "" }) {
  const { t } = useTranslation("common");
  const { dosyadan, kameradan, girdiler } = useDosyaGirdileri({ accept, multiple, onDosya, onDosyalar });
  return (
    <div className={`d-flex flex-wrap gap-2 ${className}`}>
      <Button color="primary" outline size={size} disabled={disabled} onClick={kameradan} data-testid="dosya-kaynak-kamera-dugme">
        <Camera size={14} className="me-1" />{t("dosyaKaynagi.fotografCek")}
      </Button>
      <Button color="primary" outline size={size} disabled={disabled} onClick={dosyadan} data-testid="dosya-kaynak-dosya-dugme">
        <FolderOpen size={14} className="me-1" />{t("dosyaKaynagi.dosyadanYukle")}
      </Button>
      {girdiler}
    </div>
  );
}

export function DosyaKaynakSecici({ acik, onKapat, accept, multiple = false, onDosya, onDosyalar, baslik }) {
  const { t } = useTranslation("common");
  const { dosyadan, kameradan, girdiler } = useDosyaGirdileri({ accept, multiple, onDosya, onDosyalar });
  return (
    <>
      <Modal isOpen={acik} toggle={onKapat} centered size="sm">
        <ModalHeader toggle={onKapat}>{baslik || t("dosyaKaynagi.baslik")}</ModalHeader>
        <ModalBody className="d-grid gap-2">
          <Button color="primary" onClick={() => { onKapat(); kameradan(); }} data-testid="dosya-kaynak-sec-kamera">
            <Camera size={16} className="me-1" />{t("dosyaKaynagi.fotografCek")}
          </Button>
          <Button color="light" className="border" onClick={() => { onKapat(); dosyadan(); }} data-testid="dosya-kaynak-sec-dosya">
            <FolderOpen size={16} className="me-1" />{t("dosyaKaynagi.dosyadanYukle")}
          </Button>
        </ModalBody>
      </Modal>
      {girdiler}
    </>
  );
}

export default DosyaKaynakSecici;
